import {
  type Block,
  type BlockImage,
  type BlockVisual,
  type BlockWireframe,
  type CopyableStore,
  Factory,
  type ID,
  ProjectActions,
  type StickyNote,
} from '@octopus-do/octopus-project-core'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import type { AppState } from '@store/types'
import { getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import centerElement from '@/utils/centerElement'
import { decodeFromClipboard } from '@/utils/clipboard'
import { thunkPutBlocksAfterSelectedBlock } from './thunkBlocksActions'
import { pasteNodesAndArrows } from './thunkNodesActions'
import { thunkPutStickyNote } from './thunkStickyNotesActions'

// Where the clipboard goes: pages into the selected page or section as its
// last children (blocks into the page), blocks after the selected block, a
// sticky note to the centre of the screen.
export type PasteTarget = 'node' | 'block' | 'section' | 'stickyNote'

export type PasteResult =
  // readText refused: no permission, no HTTPS, or the document lost focus
  | 'CLIPBOARD_UNAVAILABLE'
  // foreign text, or our payload that this target cannot take
  | 'NOTHING_TO_PASTE'
  | 'PASTED'

type Dispatch = ThunkDispatch<AppState, unknown, Action>
type GetState = () => AppState
type Decoded = Awaited<ReturnType<typeof decodeFromClipboard>>

// Payloads as written by thunkCopySelectedBlock and copySelectedStickyNote.
interface CopiedVisual {
  image?: BlockImage | null
  wireframes?: BlockWireframe[]
}

interface CopiedMonolithicBlock {
  type: 'monolithic'
  title: string
  colorId: ID
  content: null | string
  completed: boolean
  visual: CopiedVisual
}

interface CopiedSymbolBlock {
  type: 'symbol'
  symbolId: ID
}

interface CopiedBlock {
  type: string
  options: CopiedMonolithicBlock | CopiedSymbolBlock
}

interface CopiedStickyNote {
  type: string
  options: Partial<StickyNote> & Pick<StickyNote, 'color' | 'content' | 'size'>
}

const isNodesPayload = (decoded: Decoded) =>
  decoded?.type === 'node' ||
  decoded?.type === 'node+arrow' ||
  decoded?.type === 'copyable_store'

const blockFromBuffer = ({ options }: CopiedBlock): Block => {
  const id = getId()

  if (options.type === 'symbol') {
    return Factory.block.symbol({ id, symbolId: options.symbolId })
  }

  const visual: BlockVisual = options.visual.image
    ? { type: 'image', image: options.visual.image }
    : { type: 'wireframes', wireframes: options.visual.wireframes || [] }

  return Factory.block.monolithic({
    id,
    colorId: options.colorId,
    title: options.title,
    content: options.content,
    visual,
    completed: options.completed,
  })
}

const blocksFromBuffer = (decoded: Decoded): Block[] => {
  if (decoded?.type !== 'block') {
    return []
  }

  const items = decoded.value as CopiedBlock[]
  const copied = items[0]?.type

  if (copied !== 'block' && copied !== 'symbol') {
    return []
  }

  return items.map(blockFromBuffer)
}

const pasteNodes = (
  decoded: Decoded,
  targetId: ID,
  dispatch: Dispatch
): boolean => {
  if (!isNodesPayload(decoded)) {
    return false
  }

  const items = decoded?.value as CopyableStore[]

  if (!items.length) {
    return false
  }

  dispatch(pasteNodesAndArrows({ targetId, items }))
  return true
}

const pasteIntoNode = (
  decoded: Decoded,
  dispatch: Dispatch,
  getState: GetState
): boolean => {
  const { selectedNodes } = getState().canvas.node
  const nodeId: ID | null = selectedNodes[selectedNodes.length - 1] ?? null

  if (!nodeId) {
    return false
  }

  if (pasteNodes(decoded, nodeId, dispatch)) {
    dispatch({ type: CanvasActionTypes.CLEAR_SELECTED_NODES })
    return true
  }

  const blocks = blocksFromBuffer(decoded)

  if (!blocks.length) {
    return false
  }

  dispatch(
    commitProjectRequest(
      ProjectActions.blocks.add({ items: blocks, index: 0, parents: [nodeId] })
    )
  )
  return true
}

const pasteIntoSection = (
  decoded: Decoded,
  dispatch: Dispatch,
  getState: GetState
): boolean => {
  const sectionId = getState().canvas.section.selectedSectionId

  return !!sectionId && pasteNodes(decoded, sectionId, dispatch)
}

const pasteAfterBlock = (decoded: Decoded, dispatch: Dispatch): boolean => {
  const blocks = blocksFromBuffer(decoded)

  if (!blocks.length) {
    return false
  }

  dispatch(thunkPutBlocksAfterSelectedBlock(blocks))
  return true
}

const pasteStickyNote = (decoded: Decoded, dispatch: Dispatch): boolean => {
  if (decoded?.type !== 'stickyNote') {
    return false
  }

  const { type, options } = decoded.value as CopiedStickyNote

  if (type !== 'note') {
    return false
  }

  const offset = centerElement(0, 0, [
    '.ExternalLink-container',
    '.StickyNote-container',
  ])

  dispatch(
    thunkPutStickyNote(
      Factory.stickyNote({
        id: getId(),
        offset,
        content: options.content,
        color: options.color,
        size: options.size,
        // A note copied before OCT-5065 has no textSize on the clipboard
        // payload; the factory default stands in for it.
        ...(options.textSize && { textSize: options.textSize }),
      })
    )
  )
  return true
}

const pasteAt: Record<
  PasteTarget,
  (decoded: Decoded, dispatch: Dispatch, getState: GetState) => boolean
> = {
  node: pasteIntoNode,
  block: pasteAfterBlock,
  section: pasteIntoSection,
  stickyNote: pasteStickyNote,
}

const readClipboard = (): Promise<Decoded | 'CLIPBOARD_UNAVAILABLE'> =>
  decodeFromClipboard().catch((err: unknown) => {
    console.error('Problem with reading clipboard', err)
    return 'CLIPBOARD_UNAVAILABLE' as const
  })

// Reads the system clipboard and puts what it holds at the target. Shared by
// Cmd+V and the Paste items of the page and block toolbars; readText needs a
// user gesture, so both call it straight from the event.
export const thunkPasteFromClipboard =
  (target: PasteTarget): AppThunk<Promise<PasteResult>> =>
  async (dispatch, getState) => {
    const decoded = await readClipboard()

    if (decoded === 'CLIPBOARD_UNAVAILABLE') {
      return decoded
    }

    return pasteAt[target](decoded, dispatch, getState)
      ? 'PASTED'
      : 'NOTHING_TO_PASTE'
  }
